import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getRenewals, getRenewalStats, getCategories, runRenewalScan, getRenewalScanStatus, fmtValue, fmtDate, daysUntil } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCcwDot, RefreshCw, Building2, MapPin, Trophy, CalendarClock, Search, X, ExternalLink, Database, Clock, CalendarCheck2 } from "lucide-react";
import { toast } from "sonner";

const WINDOWS = [[6, "6 months"], [12, "12 months"], [18, "18 months"], [24, "24 months"]];
const SOURCE_META = {
  "Find a Tender": { label: "Find a Tender", cls: "bg-[#2F6DA3] text-white" },
  "Contracts Finder": { label: "Contracts Finder", cls: "bg-zinc-100 text-zinc-500 border border-zinc-300" },
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className={`border-2 border-zinc-950 p-4 bg-white ${accent ? "shadow-[4px_4px_0px_0px_#2F6DA3]" : ""}`}>
    <div className="flex items-center gap-2 text-zinc-500 mb-2">
      <Icon size={15} strokeWidth={2.5} />
      <span className="text-[10px] font-mono uppercase tracking-[0.18em]">{label}</span>
    </div>
    <div className="font-display text-3xl font-extrabold tracking-tight">{value}</div>
  </div>
);

function RenewalWindow({ end }) {
  const d = daysUntil(end);
  if (d === null) return null;
  const months = Math.round(d / 30.44);
  let cls = "bg-zinc-100 text-zinc-600 border border-zinc-300";
  if (d <= 90) cls = "bg-red-600 text-white";
  else if (d <= 182) cls = "bg-amber-500 text-white";
  else if (d <= 365) cls = "bg-[#2F6DA3] text-white";
  const label = d < 0 ? "Expired" : d <= 60 ? `${d} days` : `~${months} mo`;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${cls}`} data-testid="renewal-window">
      <CalendarClock size={11} /> Renews in {label}
    </span>
  );
}

export default function Renewals() {
  const [months, setMonths] = useState(12);
  const [category, setCategory] = useState("All");
  const [area, setArea] = useState("All");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanInfo, setScanInfo] = useState(null);
  const pollRef = useRef(null);
  const qc = useQueryClient();

  const { data: cats } = useQuery({ queryKey: ["cats"], queryFn: getCategories });
  const { data: stats } = useQuery({ queryKey: ["renewal-stats"], queryFn: getRenewalStats });
  const { data, isLoading } = useQuery({
    queryKey: ["renewals", months, category, area, debouncedSearch],
    queryFn: () => getRenewals({ within_months: months, category, area, search: debouncedSearch }),
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  const startScan = useCallback(async () => {
    setScanning(true);
    setScanInfo(null);
    try {
      await runRenewalScan();
      pollRef.current = setInterval(async () => {
        const s = await getRenewalScanStatus();
        setScanInfo(s);
        if (s.status === "done") {
          clearInterval(pollRef.current);
          setScanning(false);
          toast.success(`Renewal scan complete · ${s.stored} contracts tracked from ${s.fetched} awards`);
          qc.invalidateQueries({ queryKey: ["renewals"] });
          qc.invalidateQueries({ queryKey: ["renewal-stats"] });
        }
      }, 3000);
    } catch (e) {
      setScanning(false);
      toast.error("Renewal scan failed to start. Try again.");
    }
  }, [qc]);

  const renewals = data?.renewals || [];
  const categories = ["All", ...(cats?.categories || [])];
  const areas = ["All", ...(cats?.areas || []), "National"];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#2F6DA3] mb-1">Get in early · Award intelligence</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter flex items-center gap-3">
            <RefreshCcwDot size={38} className="text-[#2F6DA3]" /> Renewals
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Contracts already <strong>awarded</strong> to competitors that are approaching their end date — and so are heading back out to tender. Watch the incumbent, the value and the renewal window so you can position CPH <em>before</em> the re-procurement drops.
          </p>
        </div>
        <Button
          data-testid="renewal-scan-button"
          onClick={startScan}
          disabled={scanning}
          className="bg-[#2F6DA3] hover:bg-[#234E74] text-white font-bold uppercase tracking-wider px-6 py-6 border-2 border-[#2F6DA3]"
        >
          {scanning ? <RefreshCw className="animate-spin mr-2" size={16} /> : <RefreshCcwDot className="mr-2" size={16} />}
          {scanning ? "Scanning…" : "Scan Awards"}
        </Button>
      </div>

      {scanning && (
        <div className="border-2 border-[#2F6DA3] bg-orange-50 p-4 text-sm" data-testid="renewal-scan-progress">
          <div className="flex items-center gap-2 font-bold">
            <RefreshCw className="animate-spin text-[#2F6DA3]" size={15} />
            {scanInfo?.status === "fetching" || !scanInfo ? "Pulling ~3 years of awarded contracts…" : "Finishing up…"}
          </div>
          <div className="text-xs text-zinc-600 mt-1 font-mono">
            This scans award notices from Contracts Finder & Find a Tender and keeps the ones relevant to your trades. Runs in the background — you can keep using the app.
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Database} label="Tracked Awards" value={stats?.total ?? "—"} />
        <StatCard icon={Clock} label="Renewing ≤ 6m" value={stats?.within_6m ?? 0} accent />
        <StatCard icon={CalendarClock} label="Renewing ≤ 12m" value={stats?.within_12m ?? 0} />
        <StatCard icon={CalendarCheck2} label="Last Scan" value={stats?.last_scan ? fmtDate(stats.last_scan) : "Never"} />
      </div>

      {/* Window filter */}
      <div className="border-2 border-[#2F6DA3] bg-white p-4 flex flex-wrap items-center gap-3" data-testid="window-filter">
        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#2F6DA3] font-bold flex items-center gap-1"><CalendarClock size={13} /> Renewing within</span>
        <div className="flex flex-wrap gap-2">
          {WINDOWS.map(([val, label]) => (
            <button
              key={val}
              data-testid={`window-${val}`}
              onClick={() => setMonths(val)}
              className={`px-3 py-1.5 text-xs font-bold border-2 transition-colors ${
                months === val ? "bg-[#2F6DA3] text-white border-[#2F6DA3]" : "bg-white border-zinc-300 hover:border-[#2F6DA3]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="relative" data-testid="renewal-search-bar">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          data-testid="renewal-search-input"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by buyer, incumbent supplier or contract title…"
          className="w-full border-2 border-zinc-950 bg-white pl-11 pr-11 py-3.5 text-sm font-medium placeholder:text-zinc-400 focus:outline-none focus:shadow-[4px_4px_0px_0px_#2F6DA3] transition-shadow"
        />
        {search && (
          <button data-testid="renewal-search-clear" onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-900">
            <X size={16} strokeWidth={2.5} />
          </button>
        )}
      </div>

      {/* Category + area */}
      <div className="border-2 border-zinc-950 bg-white p-4 flex flex-wrap items-center gap-3">
        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">Category</span>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`renewal-cat-${c.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              onClick={() => setCategory(c)}
              className={`px-3 py-1.5 text-xs font-bold border-2 transition-colors ${category === c ? "bg-zinc-950 text-white border-zinc-950" : "bg-white border-zinc-300 hover:border-zinc-950"}`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="h-6 w-px bg-zinc-200 mx-1" />
        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#14304F]">Area</span>
        <div className="flex flex-wrap gap-2">
          {areas.map((a) => (
            <button
              key={a}
              data-testid={`renewal-area-${a.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              onClick={() => setArea(a)}
              className={`px-3 py-1.5 text-xs font-bold border-2 transition-colors ${area === a ? "bg-[#14304F] text-white border-[#14304F]" : "bg-white border-zinc-300 hover:border-[#14304F]"}`}
            >
              {a}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="text-center py-20 text-zinc-400 font-mono text-sm">Loading renewals…</div>
      ) : renewals.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-300 bg-white py-20 text-center" data-testid="renewal-empty">
          <RefreshCcwDot size={32} className="text-zinc-300 mx-auto mb-3" />
          <p className="font-display text-2xl font-bold mb-2">No renewals tracked yet</p>
          <p className="text-sm text-zinc-500 mb-6 max-w-md mx-auto">Run an award scan to pull ~3 years of awarded contracts. We'll surface the ones ending in your chosen window — the incumbent is up for renewal soon.</p>
          <Button onClick={startScan} disabled={scanning} className="bg-zinc-950 text-white font-bold uppercase tracking-wider" data-testid="renewal-empty-scan">
            <RefreshCcwDot className="mr-2" size={16} /> Scan Awards Now
          </Button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="renewals-list">
          <div className="text-xs font-mono text-zinc-500">{renewals.length} contracts renewing within {months} months</div>
          {renewals.map((r, i) => {
            const sm = SOURCE_META[r.source] || SOURCE_META["Contracts Finder"];
            return (
              <div
                key={r.id}
                data-testid={`renewal-card-${r.id}`}
                className="group border-2 border-zinc-200 bg-white p-5 hover:border-[#2F6DA3] hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_0px_#2F6DA3] transition-all duration-200 fade-up"
                style={{ animationDelay: `${i * 20}ms` }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <RenewalWindow end={r.contract_end} />
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${sm.cls}`}>{sm.label}</span>
                      {r.matched_areas?.map((a) => (
                        <span key={a} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#14304F] text-white px-2 py-1"><MapPin size={10} /> {a}</span>
                      ))}
                      {r.matched_categories?.slice(0, 3).map((m) => (
                        <span key={m} className="text-[10px] font-mono uppercase tracking-wider bg-zinc-100 px-2 py-1 text-zinc-600 border border-zinc-200">{m}</span>
                      ))}
                    </div>
                    <h3 className="font-display text-xl font-bold tracking-tight group-hover:text-[#2F6DA3] transition-colors">{r.title}</h3>
                    {r.suppliers && r.suppliers.length > 0 && (
                      <div className="mt-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-start gap-2" data-testid={`renewal-incumbent-${r.id}`}>
                        <Trophy size={13} className="mt-0.5 shrink-0 text-emerald-600" />
                        <span><strong>Incumbent:</strong> {r.suppliers.join(", ")}</span>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-xs text-zinc-500 font-mono">
                      <span className="flex items-center gap-1"><Building2 size={12} /> {r.buyer_name || "—"}</span>
                      <span className="flex items-center gap-1"><MapPin size={12} /> {r.region || r.country || "UK"}</span>
                      <span className="flex items-center gap-1"><CalendarClock size={12} /> Ends {fmtDate(r.contract_end)}</span>
                      {r.award_date && <span className="flex items-center gap-1"><CalendarCheck2 size={12} /> Awarded {fmtDate(r.award_date)}</span>}
                    </div>
                    {r.url && (
                      <a href={r.url} target="_blank" rel="noreferrer" data-testid={`renewal-link-${r.id}`} className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#2F6DA3] hover:underline">
                        View award notice <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display text-2xl font-extrabold tracking-tight">{fmtValue(r.value_amount)}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">Awarded Value</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
